import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaSearch, FaQuestionCircle, FaChevronDown, FaChevronUp, FaFilter,
  FaLightbulb, FaCog, FaUser, FaChartBar, FaFileAlt, FaShieldAlt,
  FaEnvelope, FaPhone, FaComments, FaBook, FaRocket, FaStar,
  FaGlobe, FaMobile, FaDesktop, FaTablet, FaHeadset, FaGraduationCap,
  FaHandshake, FaLeaf, FaRecycle, FaSolarPanel, FaWind, FaWater,
  FaIndustry, FaBuilding, FaCar, FaPlane, FaShip, FaTruck
} from 'react-icons/fa';

const Faqs = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showContactForm, setShowContactForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // Simulate data loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Skeleton Loading Components
  const SkeletonSearch = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 p-3 sm:p-4 mb-4 animate-pulse">
      <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
        <div className="flex flex-col md:flex-row items-center gap-3 flex-1 w-full">
          <div className="relative flex-1 max-w-md">
            <div className="w-full h-10 bg-gray-200 rounded-lg"></div>
          </div>
          <div className="w-32 h-10 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
      <div className="mt-4">
        <div className="h-4 bg-gray-200 rounded w-32 mb-3"></div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    </div>
  );

  const SkeletonFaqItem = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden animate-pulse">
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="flex items-center space-x-1">
                <div className="h-6 bg-gray-200 rounded w-16"></div>
                <div className="h-6 bg-gray-200 rounded w-12"></div>
                <div className="h-6 bg-gray-200 rounded w-10"></div>
              </div>
            </div>
          </div>
          <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
        </div>
      </div>
    </div>
  );

  // Comprehensive FAQ data with categories
  const faqData = [
    {
      id: 1,
      category: 'general',
      question: 'What is EcoMina and how does it help with ESG compliance?',
      answer: 'EcoMina is a comprehensive ESG (Environmental, Social, and Governance) platform designed to help organizations track, manage, and report on their sustainability performance. Our platform provides tools for monitoring greenhouse gas emissions, managing ESG questionnaires, generating compliance reports, and ensuring adherence to international standards like IFRS and GRI.',
      icon: <FaLeaf className="w-6 h-6" />,
      tags: ['ESG', 'Compliance', 'Sustainability']
    },
    {
      id: 2,
      category: 'general',
      question: 'How do I get started with EcoMina?',
      answer: 'Getting started is easy! First, create your account and complete the onboarding process. Then, you can invite team members, set up your organization profile, and begin answering ESG questionnaires. Our dashboard will guide you through each step with helpful tips and progress indicators.',
      icon: <FaRocket className="w-6 h-6" />,
      tags: ['Getting Started', 'Onboarding', 'Setup']
    },
    {
      id: 3,
      category: 'emissions',
      question: 'How do I track and calculate my organization\'s carbon footprint?',
      answer: 'EcoMina provides comprehensive carbon footprint tracking tools. You can input data across different emission scopes (Scope 1, 2, and 3), use our built-in emission factors, and generate detailed reports. Our platform supports various calculation methodologies and provides visualizations to help you understand your environmental impact.',
      icon: <FaChartBar className="w-6 h-6" />,
      tags: ['Carbon Footprint', 'Emissions', 'Tracking']
    },
    {
      id: 4,
      category: 'emissions',
      question: 'What emission scopes does EcoMina support?',
      answer: 'EcoMina supports all three emission scopes as defined by the GHG Protocol: Scope 1 (direct emissions from owned/controlled sources), Scope 2 (indirect emissions from purchased energy), and Scope 3 (all other indirect emissions in your value chain). We provide specific tools and templates for each scope.',
      icon: <FaIndustry className="w-6 h-6" />,
      tags: ['Scope 1', 'Scope 2', 'Scope 3', 'GHG Protocol']
    },
    {
      id: 5,
      category: 'questions',
      question: 'How do I manage ESG questionnaires and assignments?',
      answer: 'Our questionnaire management system allows you to create, assign, and track ESG questions across your organization. You can assign questions to specific team members, set deadlines, track progress, and ensure comprehensive coverage of all ESG topics. The system supports role-based access and automated reminders.',
      icon: <FaFileAlt className="w-6 h-6" />,
      tags: ['Questionnaires', 'Assignments', 'Management']
    },
    {
      id: 6,
      category: 'questions',
      question: 'Can I customize ESG questionnaires for my industry?',
      answer: 'Yes! EcoMina provides industry-specific questionnaire templates, but you can also create custom questions tailored to your organization\'s needs. Our platform supports various ESG frameworks including GRI, SASB, TCFD, and CDP, allowing you to align with the standards most relevant to your industry.',
      icon: <FaCog className="w-6 h-6" />,
      tags: ['Customization', 'Industry', 'Frameworks']
    },
    {
      id: 7,
      category: 'reporting',
      question: 'What types of reports can I generate?',
      answer: 'EcoMina generates comprehensive ESG reports including executive summaries, detailed compliance reports, carbon footprint analyses, and stakeholder communications. Reports can be exported in multiple formats (PDF, Excel, CSV) and are designed to meet various regulatory requirements and stakeholder expectations.',
      icon: <FaBook className="w-6 h-6" />,
      tags: ['Reports', 'Export', 'Compliance']
    },
    {
      id: 8,
      category: 'reporting',
      question: 'How do I ensure my reports meet regulatory requirements?',
      answer: 'Our platform is designed to help you meet various regulatory requirements including IFRS S1 and S2, EU CSRD, SEC climate disclosure rules, and other regional standards. We provide built-in compliance checks, automated validation, and guidance to ensure your reports meet current regulatory expectations.',
      icon: <FaShieldAlt className="w-6 h-6" />,
      tags: ['Regulatory', 'Compliance', 'Standards']
    },
    {
      id: 9,
      category: 'users',
      question: 'How do I manage user roles and permissions?',
      answer: 'EcoMina offers flexible role-based access control. You can assign different roles (Admin, Manager, User) with specific permissions for data access, questionnaire management, and reporting. This ensures data security while allowing appropriate access for different team members based on their responsibilities.',
      icon: <FaUser className="w-6 h-6" />,
      tags: ['User Management', 'Roles', 'Permissions']
    },
    {
      id: 10,
      category: 'users',
      question: 'Can I invite external stakeholders to collaborate?',
      answer: 'Yes! You can invite external stakeholders, consultants, or auditors to collaborate on specific projects or questionnaires. They can be granted limited access to relevant sections while maintaining data security and control over sensitive information.',
      icon: <FaHandshake className="w-6 h-6" />,
      tags: ['Collaboration', 'Stakeholders', 'External Access']
    },
    {
      id: 11,
      category: 'technical',
      question: 'Is EcoMina available on mobile devices?',
      answer: 'EcoMina is fully responsive and works seamlessly on desktop, tablet, and mobile devices. While we don\'t have a dedicated mobile app yet, our web platform is optimized for mobile use, allowing you to access all features and manage your ESG data on the go.',
      icon: <FaMobile className="w-6 h-6" />,
      tags: ['Mobile', 'Responsive', 'Accessibility']
    },
    {
      id: 12,
      category: 'technical',
      question: 'How secure is my data on EcoMina?',
      answer: 'Data security is our top priority. We use enterprise-grade security measures including encryption in transit and at rest, regular security audits, SOC 2 compliance, and secure data centers. Your data is protected with industry-standard security protocols and we never share your information with third parties.',
      icon: <FaShieldAlt className="w-6 h-6" />,
      tags: ['Security', 'Data Protection', 'Privacy']
    },
    {
      id: 13,
      category: 'support',
      question: 'What support options are available?',
      answer: 'We provide comprehensive support through multiple channels: email support, live chat, detailed documentation, video tutorials, and webinars. Our support team is available during business hours and we offer priority support for enterprise customers.',
      icon: <FaHeadset className="w-6 h-6" />,
      tags: ['Support', 'Help', 'Documentation']
    },
    {
      id: 14,
      category: 'support',
      question: 'Do you offer training for new users?',
      answer: 'Yes! We offer comprehensive training programs including onboarding sessions, webinars, and detailed documentation. Our team can provide customized training for your organization to ensure your team gets the most out of EcoMina\'s features.',
      icon: <FaGraduationCap className="w-6 h-6" />,
      tags: ['Training', 'Education', 'Onboarding']
    },
    {
      id: 15,
      category: 'billing',
      question: 'What are EcoMina\'s pricing options?',
      answer: 'EcoMina offers flexible pricing plans to suit organizations of all sizes. We have starter plans for small teams, professional plans for growing companies, and enterprise solutions for large organizations. Contact our sales team for a customized quote based on your specific needs.',
      icon: <FaStar className="w-6 h-6" />,
      tags: ['Pricing', 'Plans', 'Enterprise']
    }
  ];

  const categories = [
    { id: 'all', name: t('faqs.allQuestions'), icon: <FaQuestionCircle className="w-4 h-4" /> },
    { id: 'general', name: t('faqs.general'), icon: <FaLightbulb className="w-4 h-4" /> },
    { id: 'emissions', name: t('faqs.emissions'), icon: <FaLeaf className="w-4 h-4" /> },
    { id: 'questions', name: t('faqs.questions'), icon: <FaFileAlt className="w-4 h-4" /> },
    { id: 'reporting', name: t('faqs.reporting'), icon: <FaBook className="w-4 h-4" /> },
    { id: 'users', name: t('faqs.userManagement'), icon: <FaUser className="w-4 h-4" /> },
    { id: 'technical', name: t('faqs.technical'), icon: <FaCog className="w-4 h-4" /> },
    { id: 'support', name: t('faqs.support'), icon: <FaHeadset className="w-4 h-4" /> },
    { id: 'billing', name: t('faqs.billing'), icon: <FaStar className="w-4 h-4" /> }
  ];

  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // Handle contact form submission
    console.log('Contact form submitted:', contactForm);
    setShowContactForm(false);
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  const filteredFaqs = faqData.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryStats = () => {
    const stats = {};
    categories.forEach(cat => {
      if (cat.id === 'all') {
        stats[cat.id] = faqData.length;
      } else {
        stats[cat.id] = faqData.filter(faq => faq.category === cat.id).length;
      }
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  return (
    <div className="min-h-screen bg-gray-50 font-sans antialiased">
      {/* Header - Responsive */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-lg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow">
                <FaQuestionCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{t('faqs.frequentlyAskedQuestions')}</h1>
                <p className="text-green-100 text-xs sm:text-sm">{t('faqs.findAnswers')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-100">{t('faqs.lastUpdated')}: {new Date().toLocaleDateString()}</p>
              <p className="text-sm text-lime-300 font-bold">{faqData.length} {t('faqs.totalQuestions')}</p>
              <p className="text-xs text-green-200">{t('faqs.categories')}: {categories.length - 1}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-3 sm:px-4 py-4 sm:py-4">
        {/* Search and Filter Section */}
        {isLoading ? (
          <SkeletonSearch />
        ) : (
          <div className="bg-white rounded-lg shadow border border-gray-100 p-3 sm:p-4 mb-4">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
              <div className="flex flex-col md:flex-row items-center gap-3 flex-1 w-full">
                <div className="relative flex-1 max-w-md">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder={t('faqs.searchFaqs')}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-100 text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <button
                  onClick={() => setShowContactForm(true)}
                  className="px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 flex items-center gap-2 text-sm w-full md:w-auto justify-center"
                >
                  <FaEnvelope className="w-3 h-3" />
{t('faqs.contactSupport')}
                </button>
              </div>
            </div>

            {/* Category Filter */}
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                <FaFilter className="w-3.5 h-3.5 mr-2 text-green-600" />
{t('faqs.browseByCategory')}
              </h3>
              <div className="grid grid-cols-2 xs:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`p-3 sm:p-4 rounded-lg flex items-center justify-center gap-2 text-xs ${selectedCategory === category.id
                      ? 'bg-green-500 text-white shadow'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {category.icon}
                    <span className="font-medium whitespace-nowrap text-xs sm:text-xs">{category.name}</span>
                    <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
                      {categoryStats[category.id]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FAQ Results Header */}
        {!isLoading && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800">
{filteredFaqs.length} {filteredFaqs.length === 1 ? t('faqs.question') : t('faqs.questions')} {t('faqs.found')}
              </h2>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
{t('faqs.clearSearch')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* FAQ Items */}
        <div className="space-y-2">
          {isLoading ? (
            // Skeleton FAQ items
            <>
              <SkeletonFaqItem />
              <SkeletonFaqItem />
              <SkeletonFaqItem />
              <SkeletonFaqItem />
              <SkeletonFaqItem />
            </>
          ) : (
            // Actual FAQ items
            filteredFaqs.map((faq, index) => (
              <div
                key={faq.id}
                className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden hover:shadow-md transition-all"
              >
                <div
                  className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleFaq(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                        {React.cloneElement(faq.icon, { className: "w-3.5 h-3.5 text-white" })}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-800 mb-1">
                          {faq.question}
                        </h3>
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                            {categories.find(cat => cat.id === faq.category)?.name}
                          </span>
                          {faq.tags.slice(0, 2).map((tag, tagIndex) => (
                            <span key={tagIndex} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                              {tag}
                            </span>
                          ))}
                          {faq.tags.length > 2 && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                              +{faq.tags.length - 2}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                      {activeIndex === index ? (
                        <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                      ) : (
                        <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                      )}
                    </div>
                  </div>
                </div>

                {activeIndex === index && (
                  <div className="border-t border-gray-100 p-3 bg-gray-50">
                    <div className="prose max-w-none">
                      <p className="text-gray-800 leading-relaxed text-sm">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* No Results */}
        {!isLoading && filteredFaqs.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaSearch className="w-6 h-6 text-white" />
            </div>
            <p className="text-gray-600 text-sm font-semibold mb-1">{t('faqs.noFaqsFound')}</p>
            <p className="text-gray-400 text-xs mb-3">{t('faqs.tryAdjustingSearch')}</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}
              className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
            >
{t('faqs.resetFilters')}
            </button>
          </div>
        )}

        {/* Contact Support Modal */}
        {showContactForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
            <div className="bg-white rounded-lg p-3 sm:p-4 max-w-md w-full mx-auto">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-bold text-gray-800">{t('faqs.contactSupport')}</h2>
                <button
                  onClick={() => setShowContactForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaChevronUp className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{t('faqs.name')}</label>
                    <input
                      type="text"
                      className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{t('faqs.email')}</label>
                    <input
                      type="email"
                      className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('faqs.subject')}</label>
                  <input
                    type="text"
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('faqs.message')}</label>
                  <textarea
                    rows={3}
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
                  >
{t('faqs.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                  >
{t('faqs.sendMessage')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Faqs;